/**
 * StudioSai — assistant de la boutique, version sans IA.
 *
 * Rapproche la question du client de deux sources, et de deux seulement :
 *   - la FAQ, écrite par la boutique dans l'éditeur de thème (section
 *     « Shop assistant ») ;
 *   - le catalogue, servi en direct par templates/collection.sai-kb.liquid.
 *
 * Il ne génère aucun texte. Chaque réponse est soit un passage de FAQ tel
 * quel, soit une phrase-gabarit remplie avec une donnée existante (« The
 * Lumix SZ1 uses a 1/2.33" CCD sensor. »). S'il ne trouve rien de fiable,
 * il le dit et renvoie vers une personne. C'est volontaire : sur du
 * matériel d'occasion, une caractéristique inventée coûte une réclamation.
 *
 * Il ne voit aucune commande et ne promet rien. Tout ce qui touche une
 * commande, un paiement ou un litige part directement vers l'équipe.
 *
 * Toute insertion dans la page passe par textContent : ni la question du
 * client ni une réponse saisie dans l'éditeur ne peuvent injecter de HTML.
 */
(function () {
  'use strict';

  if (window.customElements.get('sai-assistant')) return;

  /* ═══════════════════════════ Texte ═══════════════════════════ */

  function fold(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’`']/g, ' ');
  }

  // Chaîne de recherche bordée d'espaces, pour tester des expressions
  // entières : « in the box » ne doit pas trouver « within the boxes ».
  // € et < > restent, pour lire « moins de 200€ » ou « <150 ».
  function flat(s) {
    return ' ' + fold(s).replace(/[^a-z0-9€<>]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
  }

  function tokens(s) {
    var base = fold(s).replace(/[^a-z0-9]+/g, ' ').trim();
    if (!base) return [];
    var raw = base.split(' ');
    var out = raw.slice();
    for (var i = 0; i < raw.length - 1; i++) {
      var a = raw[i];
      var b = raw[i + 1];
      // « t 90 », « sz-1 », « 510 hs » : on recolle les morceaux d'un
      // nom de modèle, que le client tape rarement d'un seul tenant.
      if ((/^[a-z]{1,4}$/.test(a) && /^\d+[a-z]*$/.test(b)) || (/^\d+$/.test(a) && /^[a-z]{1,3}$/.test(b))) {
        out.push(a + b);
      }
    }
    // Pluriels simples : « cameras » trouve « camera ».
    raw.forEach(function (t) {
      if (t.length > 4 && /[^s]s$/.test(t)) out.push(t.slice(0, -1));
    });
    return out;
  }

  var STOP = new Set(
    (
      'a an the is are was be been do does did can could would should will i im you your my me we us our it its this that these those there here ' +
      'of for to in on at by with from about as and or but if so not no yes what which who how why when where any some much many more most very please ' +
      'le la les un une des du de d l est sont ce ca cet cette ces je j tu vous il elle on nous ils mon ma mes ton ta tes son sa ses votre vos leur ' +
      'et ou mais donc ni car en dans pour par avec sans sur sous chez au aux y que qui quoi quel quelle quels quelles comment pourquoi quand ' +
      'ne pas plus tres bien svp stp t s n m c qu'
    ).split(' ')
  );

  function meaningful(toks) {
    return toks.filter(function (t) {
      return !STOP.has(t) && (t.length > 1 || /\d/.test(t));
    });
  }

  /* ═══════════════════════ Concepts ════════════════════════
     Chaque concept regroupe les mots et expressions qui disent la même
     chose, en anglais et en français. C'est ce qui permet à « délai de
     livraison » et « how long does shipping take » d'arriver au même
     endroit sans IA. */

  var CONCEPTS = {
    shipping: {
      w: 'ship ships shipping shipped delivery deliver delivered livraison livrer livre livrez envoi envoyer envoyez expedition expedie expediez expedier postage transporteur carrier arrive arrival arriver dispatch dispatched',
      p: ['how long', 'combien de temps', 'frais de port', 'when will', 'quand est ce que je']
    },
    customs: { w: 'customs douane douanes duty duties taxe taxes tax tva vat droits import' },
    warranty: { w: 'warranty warranties guarantee garantie garanti garantis guaranteed' },
    returns: {
      w: 'return returns retour retours retourner refund refunds rembourse remboursement rembourser exchange echange echanger',
      p: ['change my mind', 'changer d avis']
    },
    tested: { w: 'tested test teste tester tests work works working fonctionne fonctionnel fonctionnement marche functional checked verifie' },
    newness: {
      w: 'new neuf neuve used occasion preowned secondhand vintage refurbished reconditionne',
      p: ['second hand', 'pre owned', 'brand new']
    },
    condition: {
      w: 'condition etat grade graded grading scratch scratches rayure rayures raye cosmetic cosmetique wear usure mint marks traces abime abimee dent',
      p: ['what shape']
    },
    included: {
      w: 'included include includes including inclus inclut incluse fourni fournie fournis kit box accessories accessoires bundle extras starter',
      p: ['comes with', 'come with', 'in the box', 'dans la boite', 'what do i get', 'livre avec', 'vendu avec', 'avec quoi']
    },
    battery: { w: 'battery batteries batterie pile piles accu' },
    charger: { w: 'charger chargeur charging recharge recharger cable' },
    gift: { w: 'gift gifts cadeau cadeaux present offrir wrap wrapping emballage packaging pochette birthday anniversaire noel christmas girlfriend copine boyfriend' },
    payment: {
      w: 'pay payment payments paiement payer paypal klarna installments',
      p: ['carte bancaire', 'credit card', 'apple pay', 'plusieurs fois', 'moyens de paiement']
    },
    transfer: {
      w: 'transfer transferer computer ordinateur laptop pc mac phone telephone iphone android files fichiers',
      p: ['get the photos', 'recuperer les photos', 'send photos']
    },
    contact: {
      w: 'contact contacter email mail human humain person personne someone support help aide talk parler instagram',
      p: ['real person', 'service client', 'quelqu un']
    },
    trust: { w: 'legit trust trustworthy fiable confiance arnaque scam reviews review avis rating trustpilot vinted serieux reliable' },
    price: { w: 'price prix cost cout coute combien expensive cher euros euro', p: ['how much'] },
    stock: {
      w: 'stock available availability disponible dispo disponibilite left reste epuise',
      p: ['in stock', 'sold out', 'still available', 'encore dispo']
    },
    specs: {
      w: 'specs spec specification specifications caracteristiques caracteristique technical technique details',
      p: ['fiche technique', 'tech specs', 'full specs']
    },

    // Champs d'une fiche technique.
    f_sensor: { w: 'sensor capteur ccd cmos' },
    f_resolution: { w: 'resolution megapixel megapixels mp mpx pixels' },
    f_lens: { w: 'lens objectif optique focal zeiss leica fujinon' },
    f_zoom: { w: 'zoom' },
    f_aperture: { w: 'aperture ouverture' },
    f_screen: { w: 'screen ecran display lcd oled touchscreen tactile' },
    f_video: { w: 'video videos film filmer movie movies' },
    f_storage: { w: 'storage stockage sd microsd memory memoire card carte', p: ['memory stick'] },
    f_year: {
      w: 'year annee released release sortie sorti age old vieux',
      p: ['how old', 'quelle annee', 'came out']
    },
    f_stab: { w: 'stabilization stabilisation stabilizer stabilisateur steadyshot ois shaky', p: ['image stabilization'] },
    f_wifi: { w: 'wifi wireless bluetooth connectivity connectivite', p: ['wi fi'] },
    f_flash: { w: 'flash' },

    // Envies, pour les recommandations.
    x_selfie: { w: 'selfie selfies', p: ['self portrait', 'front screen', 'ecran avant'] },
    x_night: {
      w: 'night nuit soir soiree evening dark sombre indoor interieur party fete club concert',
      p: ['low light', 'faible lumiere', 'after dark']
    },
    x_slim: { w: 'thin slim fin fine pocket poche small petit tiny leger' },
    x_touch: { w: 'touch touchscreen tactile' },
    x_rare: { w: 'rare limited limite collector', p: ['design edition', 'limited edition'] },
    x_popular: {
      w: 'popular populaire bestseller bestsellers favorite favourite prefere trending',
      p: ['best seller', 'best sellers', 'most popular']
    },
    x_beginner: { w: 'beginner debutant easy facile simple', p: ['first camera', 'premier appareil'] },
    x_cheap: {
      w: 'cheap cheapest affordable abordable budget',
      p: ['pas cher', 'moins cher', 'low price', 'petit prix', 'best value']
    },
    x_reco: {
      w: 'recommend recommendation recommande recommander suggest suggestion conseil conseille conseiller best meilleur meilleure which lequel laquelle choose choisir looking cherche recherche want veux voudrais need besoin show montre montrer options camera cameras appareil appareils',
      p: ['looking for', 'je cherche', 'what camera', 'quel appareil']
    },

    // Conversation.
    greet: { w: 'hi hello hey hola bonjour salut coucou yo bonsoir' },
    thanks: { w: 'thanks thank thx ty merci', p: ['thank you'] },
    bot: { w: 'bot robot ai ia chatbot', p: ['are you human', 'are you real', 'tu es un robot', 'es tu un robot'] },
    pronoun: { w: 'it this that celui celle ci one' },
    accessory: { w: 'case etui housse pouch protector protection strap dragonne reader lecteur alcantara' }
  };

  var CIDX = Object.keys(CONCEPTS).map(function (id) {
    var c = CONCEPTS[id];
    return {
      id: id,
      words: new Set(c.w.split(' ')),
      phrases: (c.p || []).map(flat)
    };
  });

  function detect(qf, toks) {
    var set = new Set(toks);
    var out = {};
    CIDX.forEach(function (c) {
      var hit = false;
      c.words.forEach(function (w) {
        if (!hit && set.has(w)) hit = true;
      });
      for (var i = 0; !hit && i < c.phrases.length; i++) {
        if (qf.indexOf(c.phrases[i]) !== -1) hit = true;
      }
      if (hit) out[c.id] = true;
    });
    return out;
  }

  // Concepts trop généraux pour désigner une question de FAQ précise.
  var FAQ_NEUTRAL = new Set([
    'x_reco', 'price', 'stock', 'specs', 'greet', 'thanks', 'bot', 'pronoun',
    'x_selfie', 'x_night', 'x_slim', 'x_touch', 'x_rare', 'x_popular', 'x_beginner', 'x_cheap'
  ]);

  var FIELD_IDS = [
    'f_sensor', 'f_resolution', 'f_lens', 'f_zoom', 'f_aperture', 'f_screen',
    'f_video', 'f_storage', 'f_year', 'f_stab', 'f_wifi', 'f_flash'
  ];

  /* ═══════════════════════ Index BM25 ════════════════════════ */

  function makeIndex(docs) {
    var df = {};
    var total = 0;
    var N = docs.length;
    var tfs = docs.map(function (toks) {
      var tf = {};
      toks.forEach(function (t) {
        tf[t] = (tf[t] || 0) + 1;
      });
      Object.keys(tf).forEach(function (t) {
        df[t] = (df[t] || 0) + 1;
      });
      total += toks.length;
      return { tf: tf, len: toks.length || 1 };
    });
    var avg = total / Math.max(N, 1) || 1;
    return function score(i, qtoks) {
      var d = tfs[i];
      var s = 0;
      var seen = {};
      qtoks.forEach(function (t) {
        if (seen[t] || !d.tf[t]) return;
        seen[t] = true;
        var idf = Math.log(1 + (N - df[t] + 0.5) / (df[t] + 0.5));
        var tf = d.tf[t];
        s += (idf * tf * 2.2) / (tf + 1.2 * (0.25 + (0.75 * d.len) / avg));
      });
      return s;
    };
  }

  /* ═══════════════════════ Catalogue ════════════════════════ */

  var BRANDS = {
    sony: ['sony', 'cybershot'],
    canon: ['canon', 'ixus', 'ixy', 'powershot'],
    fujifilm: ['fujifilm', 'fuji', 'finepix'],
    panasonic: ['panasonic', 'lumix'],
    casio: ['casio', 'exilim'],
    nikon: ['nikon', 'coolpix'],
    olympus: ['olympus'],
    pentax: ['pentax', 'optio'],
    samsung: ['samsung', 'digimax']
  };
  var BRAND_LABEL = {
    sony: 'Sony', canon: 'Canon', fujifilm: 'Fujifilm', panasonic: 'Lumix', casio: 'Casio',
    nikon: 'Nikon', olympus: 'Olympus', pentax: 'Pentax', samsung: 'Samsung'
  };
  var BRAND_OF = {};
  Object.keys(BRANDS).forEach(function (b) {
    BRANDS[b].forEach(function (w) {
      BRAND_OF[w] = b;
    });
  });

  var COLOURS = {
    pink: ['pink', 'rose', 'blush', 'sakura'],
    black: ['black', 'noir', 'noire', 'onyx', 'midnight'],
    white: ['white', 'blanc', 'blanche', 'pearl', 'diamond'],
    gold: ['gold', 'dore', 'doree', 'champagne', 'sahara'],
    purple: ['purple', 'violet', 'violette', 'lavender', 'lavande', 'lilac', 'lilas', 'mauve'],
    red: ['red', 'rouge', 'ruby', 'velvet'],
    silver: ['silver', 'argent', 'argente', 'gray', 'grey', 'gris', 'grise'],
    brown: ['brown', 'marron', 'espresso', 'chocolat']
  };

  function coloursIn(qf) {
    return Object.keys(COLOURS).filter(function (c) {
      return COLOURS[c].some(function (w) {
        return qf.indexOf(' ' + w + ' ') !== -1;
      });
    });
  }

  function brandsIn(toks) {
    var out = [];
    toks.forEach(function (t) {
      var b = BRAND_OF[t];
      if (b && out.indexOf(b) === -1) out.push(b);
    });
    return out;
  }

  var GRADE_LABEL = { S: 'Near Mint', 'A+': 'Excellent+', A: 'Excellent', B: 'Very Good', C: 'Good', D: 'Used' };

  function parseLines(text) {
    if (!text) return [];
    return String(text)
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean)
      .map(function (l) {
        var i = l.indexOf(':');
        return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : ['', l];
      });
  }

  // Même lecture que blocks/sai-condition-report.liquid : les deux doivent
  // rester d'accord sur ce qu'un relevé dit.
  function parseCond(text) {
    if (!text) return null;
    var c = {};
    parseLines(text).forEach(function (kv) {
      if (kv[0]) c[kv[0].toLowerCase()] = kv[1];
    });
    var grade = c.grade || '';
    var parts = grade.split('·');
    var letter = (parts[1] || '').trim();
    return {
      grade: grade.replace(/\s*·\s*/, '·'),
      label: GRADE_LABEL[letter] || '',
      rows: [
        ['Exterior', c.exterior],
        ['Screen', c.screen],
        ['Body', c.body],
        ['Function', c.function_grade],
        [c.metric_1_label, c.metric_1_value],
        [c.metric_2_label, c.metric_2_value],
        [c.metric_3_label, c.metric_3_value]
      ].filter(function (r) {
        return r[0] && r[1];
      }),
      note: c.note || ''
    };
  }

  function buildProduct(p) {
    var title = p.t || '';
    var head = title.split(' ✮')[0];
    var base = head.split(' — ')[0].split(' · ')[0].trim();
    var bt = tokens(base);
    var brand = BRAND_OF[bt[0]] || null;
    var models = bt.filter(function (t) {
      return (/\d/.test(t) && /[a-z]/.test(t)) || /^\d{3,}$/.test(t);
    });
    var alias = flat(base.split(' ').slice(1).join(' '));
    var spec = parseLines(p.spec);
    var lens = spec.filter(function (kv) {
      return /^lens/i.test(kv[0]);
    })[0];
    var zm = lens && lens[1].match(/(\d+(?:\.\d+)?)\s*x/i);
    var tags = p.tags || [];
    var blob = flat([title, tags.join(' '), p.d, p.spec].join(' '));

    return {
      h: p.h,
      title: title,
      name: base,
      url: p.u,
      img: p.img,
      pm: p.pm,
      cm: p.cm,
      price: p.price,
      avail: !!p.a,
      isCamera: p.ty === 'Camera',
      brand: brand,
      models: models,
      alias: alias,
      key: models.join('|') || alias,
      colours: coloursIn(flat(title + ' ' + tags.join(' '))),
      rare: tags.indexOf('Rare Find') !== -1 || tags.indexOf('Limited Edition') !== -1,
      popular: tags.indexOf('Best Seller') !== -1 || tags.indexOf('TikTok Favorite') !== -1,
      desc: p.d || '',
      spec: spec,
      specText: p.spec || '',
      cond: parseCond(p.cond),
      zoom: zm ? parseFloat(zm[1]) : 0,
      blob: blob,
      toks: tokens([title, title, title, tags.join(' '), p.d, p.spec].join(' '))
    };
  }

  // Nom d'exemplaire sans les badges de catalogue (« ✮ Under €200 ») :
  // utile pour distinguer deux coloris, inutile dans une phrase.
  function unitName(P) {
    return P.title.split(' ✮')[0].trim();
  }

  function card(P) {
    return { t: P.title, u: P.url, img: P.img, pm: P.pm, cm: P.cm, a: P.avail };
  }

  /* ═══════════════════════ FAQ ════════════════════════ */

  function buildFaq(list) {
    return (list || [])
      .filter(function (f) {
        return f && f.q && f.a;
      })
      .map(function (f) {
        var key = f.q + ' ' + (f.k || '');
        return {
          q: f.q,
          a: f.a,
          ll: f.ll,
          lu: f.lu,
          concepts: Object.keys(detect(flat(key), tokens(key))).filter(function (c) {
            return !FAQ_NEUTRAL.has(c);
          }),
          toks: tokens(key),
          atoks: tokens(f.a)
        };
      });
  }

  /* ═══════════════════════ Régions ════════════════════════
     Pour « livraison au Japon », on extrait la ligne du Japon de la FAQ
     livraison et on la met en tête. La clé doit apparaître dans le
     texte de la FAQ ; sinon on rend la réponse entière, sans rien perdre. */

  var REGIONS = {
    'western europe': ['france', 'germany', 'allemagne', 'spain', 'espagne', 'italy', 'italie', 'belgium', 'belgique', 'netherlands', 'pays bas', 'holland', 'portugal', 'austria', 'autriche', 'switzerland', 'suisse', 'luxembourg', 'ireland', 'irlande', 'europe'],
    'united kingdom': ['uk', 'united kingdom', 'royaume uni', 'angleterre', 'england', 'britain', 'scotland', 'ecosse', 'london', 'londres'],
    'united states': ['usa', 'united states', 'etats unis', 'america', 'amerique', 'new york', 'california', 'californie'],
    canada: ['canada', 'quebec'],
    mexico: ['mexico', 'mexique'],
    brazil: ['brazil', 'bresil', 'latin america', 'amerique latine', 'argentina', 'argentine', 'chile', 'chili', 'colombia', 'colombie', 'peru', 'perou'],
    japan: ['japan', 'japon', 'korea', 'coree', 'tokyo', 'seoul'],
    'rest of asia': ['asia', 'asie', 'china', 'chine', 'hong kong', 'taiwan', 'singapore', 'singapour', 'thailand', 'thailande', 'vietnam', 'india', 'inde', 'philippines', 'malaysia', 'malaisie', 'indonesia', 'indonesie'],
    'saudi arabia': ['saudi', 'arabie', 'uae', 'emirates', 'emirats', 'dubai', 'qatar'],
    australia: ['australia', 'australie', 'new zealand', 'nouvelle zelande'],
    africa: ['africa', 'afrique', 'morocco', 'maroc', 'algeria', 'algerie', 'tunisia', 'tunisie', 'senegal', 'nigeria', 'south africa']
  };

  function regionIn(qf) {
    for (var key in REGIONS) {
      for (var i = 0; i < REGIONS[key].length; i++) {
        if (qf.indexOf(' ' + REGIONS[key][i] + ' ') !== -1) return key;
      }
    }
    return null;
  }

  function regionLine(answer, region) {
    var segs = String(answer)
      .split(/\r?\n/)
      .join(' · ')
      .split(' · ');
    for (var i = 0; i < segs.length; i++) {
      var seg = segs[i].trim();
      if (fold(seg).indexOf(region) === 0 && /—\s*\d/.test(seg)) {
        var parts = seg.split('—');
        return 'Shipping to ' + parts[0].trim() + ': ' + parts[1].trim().replace(/days?$/, 'business days after dispatch') + '.';
      }
    }
    return null;
  }

  /* ═══════════════════════ Champs de fiche ════════════════════════ */

  var FIELDS = {
    f_sensor: { keys: [/^sensor/i], label: 'Sensor', say: function (n, v) { return 'The ' + n + ' uses a ' + v + ' sensor.'; } },
    f_resolution: { keys: [/^resolution/i], label: 'Resolution', say: function (n, v) { return 'The ' + n + ' shoots at ' + v + '.'; } },
    f_lens: { keys: [/^lens/i], label: 'Lens', say: function (n, v) { return 'Lens on the ' + n + ': ' + v + '.'; } },
    f_zoom: {
      keys: [/^lens/i, /zoom/i],
      label: 'Zoom',
      say: function (n, v) {
        var z = v.match(/(\d+(?:\.\d+)?)\s*x/i);
        return z ? 'The ' + n + ' has a ' + z[1] + 'x optical zoom — ' + v + '.' : 'Lens on the ' + n + ': ' + v + '.';
      }
    },
    f_aperture: { keys: [/^aperture/i], label: 'Aperture', say: function (n, v) { return 'Aperture on the ' + n + ': ' + v + '.'; } },
    f_screen: { keys: [/^screen/i, /^display/i], label: 'Screen', say: function (n, v) { return 'The ' + n + ' has a ' + v + '.'; } },
    f_video: { keys: [/^video/i], label: 'Video', say: function (n, v) { return 'The ' + n + ' records video at ' + v + '.'; } },
    f_storage: { keys: [/^storage/i, /^internal memory/i], label: 'Storage', say: function (n, v) { return 'Storage on the ' + n + ': ' + v + '.'; } },
    f_year: { keys: [/^release year/i, /^year/i], label: 'Release year', say: function (n, v) { return 'The ' + n + ' came out in ' + v + '.'; } },
    f_stab: { keys: [/^stabili[sz]ation/i], label: 'Stabilisation', say: function (n, v) { return 'Stabilisation on the ' + n + ': ' + v + '.'; } },
    f_wifi: { keys: [/^connectivity/i, /wi-?fi/i], label: 'Wi-Fi', say: function (n, v) { return 'Connectivity on the ' + n + ': ' + v + '.'; } },
    f_flash: { keys: [/^flash/i], label: 'Flash', say: function (n, v) { return 'Flash on the ' + n + ': ' + v + '.'; } }
  };

  function fieldValue(P, id) {
    var f = FIELDS[id];
    for (var i = 0; i < f.keys.length; i++) {
      for (var j = 0; j < P.spec.length; j++) {
        if (f.keys[i].test(P.spec[j][0])) return P.spec[j][1];
      }
    }
    return null;
  }

  // Les vieux Sony prennent du Memory Stick : la carte SD du kit n'y entre
  // pas. On le signale au lieu de laisser croire le contraire.
  function cardCaution(P) {
    var s = fieldValue(P, 'f_storage');
    if (s && /memory stick/i.test(s) && !/\bsd\b/i.test(s)) {
      return 'Heads-up: the ' + P.name + ' takes ' + s + ', not SD. Message us to confirm which card ships with it.';
    }
    return '';
  }

  /* ═══════════════════════ Recommandations ════════════════════════ */

  var FEATURES = {
    x_night: { label: 'good in low light', kw: ['after dark', 'low light', 'bad light', 'dim room', 'at night', 'dark room', 'stabilisation', 'stabilization', 'steadyshot', 'o i s'] },
    x_selfie: { label: 'good for selfies', kw: ['self portrait', 'self portraits', 'front facing', 'selfie', 'back at yourself', 'arm s length'] },
    x_slim: { label: 'slim', kw: ['thin', 'slim', 'thinnest', 'pocket', 'flat front', 'folded', 'folds'] },
    x_touch: { label: 'with a touchscreen', kw: ['touchscreen'] },
    x_beginner: { label: 'easy to use', kw: ['learning a single setting', 'learn a setting', 'nothing to configure', 'nothing to learn', 'nothing you can get wrong', 'first digicam', 'where to start', 'shortcut', 'no learning curve', 'never used a camera'] },
    f_wifi: { label: 'with Wi-Fi', kw: ['wi fi', 'wifi'] },
    f_video: { label: 'good for video', kw: ['full hd', '1080p', '1080', '720p'] },
    f_stab: { label: 'with stabilisation', kw: ['stabilisation', 'stabilization', 'steadyshot', 'o i s'] }
  };

  function parsePrice(qf) {
    var cur = '(?:€|eur|euros?)?';
    var n = '(\\d{2,4})';
    var r = { min: null, max: null };
    var m = qf.match(new RegExp('(?:between|entre)\\s' + cur + '\\s?' + n + '\\s?' + cur + '\\s(?:and|et|to|a)\\s' + cur + '\\s?' + n));
    if (m) {
      r.min = +m[1];
      r.max = +m[2];
      return r;
    }
    m = qf.match(new RegExp('(?:under|below|less than|max|maximum|moins de|sous|jusqu a|up to|budget|budget de|inferieur a|pas plus de|cheaper than|<)\\s?' + cur + '\\s?' + n));
    if (m) r.max = +m[1];
    m = qf.match(new RegExp('(?:over|above|more than|plus de|au dessus de|superieur a|minimum|at least|>)\\s?' + cur + '\\s?' + n));
    if (m) r.min = +m[1];
    if (r.min == null && r.max == null) {
      m = qf.match(/ (\d{2,4}) ?(?:€|eur |euros? )|€ ?(\d{2,4}) /);
      if (m) r.max = +(m[1] || m[2]);
    }
    return r;
  }

  /* ═══════════════════════ Composant ════════════════════════ */

  var STORE_KEY = 'sai-asst:v1';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function safeUrl(u) {
    u = String(u || '').trim();
    return /^(\/(?!\/)|https:\/\/)/.test(u) ? u : '';
  }

  function safeImg(u) {
    u = String(u || '').trim();
    return /^(https:)?\/\//.test(u) ? u : '';
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  class SaiAssistant extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.cfg = JSON.parse(this.querySelector('[data-sai-config]').textContent);
      } catch (e) {
        this.cfg = {};
      }
      this.cfg.contact = safeUrl(this.cfg.contact) || '/pages/contact';
      this.cfg.tracking = safeUrl(this.cfg.tracking);
      this.chips = String(this.cfg.chips || '')
        .split(/\r?\n/)
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean)
        .slice(0, 6);

      this.faq = buildFaq(this.cfg.faq);
      this.faqScore = makeIndex(this.faq.map(function (f) { return f.toks; }));
      this.faqAScore = makeIndex(this.faq.map(function (f) { return f.atoks; }));
      this.products = [];
      this.ctx = null;
      this.history = [];

      this.bubble = this.querySelector('.sai-asst__bubble');
      this.panel = this.querySelector('.sai-asst__panel');
      this.logEl = this.querySelector('.sai-asst__log');
      this.form = this.querySelector('.sai-asst__form');
      this.input = this.querySelector('.sai-asst__input');

      this.bubble.addEventListener('click', () => (this.hasAttribute('data-open') ? this.close() : this.open()));
      this.querySelector('.sai-asst__close').addEventListener('click', () => this.close());
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        var q = this.input.value.trim();
        if (!q) return;
        this.input.value = '';
        this.ask(q);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('data-open')) {
          e.stopPropagation();
          this.close();
        }
      });
      this.logEl.addEventListener('click', (e) => {
        var chip = e.target.closest('.sai-asst__chip');
        if (chip) this.ask(chip.dataset.q || chip.textContent);
      });

      this.restore();
    }

    /* ─────────── Ouverture ─────────── */

    open() {
      if (this.hasAttribute('data-open')) return;
      this.setAttribute('data-open', '');
      this.bubble.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      void this.panel.offsetWidth;
      this.panel.classList.add('is-open');
      if (!this.history.length) this.greet();
      this.loadKb();
      this.scrollEnd();
      setTimeout(() => this.input.focus({ preventScroll: true }), reducedMotion ? 0 : 80);
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      var hadFocus = this.panel.contains(document.activeElement);
      this.removeAttribute('data-open');
      this.bubble.setAttribute('aria-expanded', 'false');
      this.panel.classList.remove('is-open');
      setTimeout(() => {
        if (!this.hasAttribute('data-open')) this.panel.hidden = true;
      }, reducedMotion ? 0 : 230);
      if (hadFocus) this.bubble.focus({ preventScroll: true });
    }

    /* ─────────── Données ─────────── */

    loadKb() {
      if (this._kb) return this._kb;
      this._kb = fetch(this.cfg.kb, { credentials: 'same-origin' })
        .then((r) => r.text())
        .then((txt) => {
          var list = JSON.parse(txt);
          this.products = list.map(buildProduct);
          this.prodScore = makeIndex(this.products.map(function (P) { return P.toks; }));
        })
        .catch(() => {
          // Sans catalogue, la FAQ continue de répondre ; les questions
          // produit retombent sur « je ne sais pas » plutôt que d'inventer.
          this.products = [];
        });
      return this._kb;
    }

    /* ─────────── Conversation ─────────── */

    greet() {
      this.push({
        who: 'bot',
        ans: {
          text: [this.cfg.intro || 'Hi! Ask me about a camera, its condition, shipping or what’s in the box.'],
          chips: this.chips
        }
      });
    }

    ask(q) {
      this.open();
      this.push({ who: 'user', text: q });
      var typing = this.typing();
      var started = Date.now();
      this.loadKb().then(() => {
        var ans;
        try {
          ans = this.answer(q);
        } catch (e) {
          ans = this.fallback();
        }
        var wait = reducedMotion ? 0 : Math.max(0, 420 - (Date.now() - started));
        setTimeout(() => {
          typing.remove();
          this.push({ who: 'bot', ans: ans });
        }, wait);
      });
    }

    /**
     * Décide de la réponse. Méthode publique : on peut l'appeler depuis la
     * console pour tester une question sans passer par l'interface.
     */
    answer(text) {
      var qf = flat(text);
      var qtoks = tokens(text);
      var C = detect(qf, qtoks);
      var mean = meaningful(qtoks);

      // 1. Ce qui touche une commande part toujours vers une personne.
      if (/ (my|mon|ma|mes) (order|orders|commande|commandes|colis|parcel|package|purchase|achat|refund|remboursement|payment|paiement) | order number | numero de commande | where is my | ou est mon | ou est ma | ou en est | not received | pas recu | jamais recu | never arrived | never received | pas arrive | track my | tracking number | numero de suivi | suivre mon | suivre ma | cancel | annuler | annulation | change my address | changer mon adresse | modify my order | modifier ma commande | arrived broken | arrive casse | recu casse | arrived damaged | wrong item | mauvais article | payment failed | paiement refuse | card declined | carte refusee | charged twice | litige | dispute /.test(qf)) {
        return this.handoff();
      }

      // 2. Politesse.
      if (C.greet && mean.length <= 1) return { text: ['Hi! What can I help you with?'], chips: this.chips };
      if (C.thanks && mean.length <= 2) return { text: ['You’re welcome! Anything else about a camera?'] };
      if (C.bot) {
        return {
          text: [
            'Yes — I’m an automated helper, not a person. I answer only from StudioSai’s catalogue and policies.',
            'I don’t make things up: when I’m not sure, I’ll point you to the team.'
          ],
          actions: [{ label: 'Contact the team', url: this.cfg.contact }]
        };
      }

      var fields = FIELD_IDS.filter(function (f) { return C[f]; });
      var group = this.identify(qtoks, qf);
      var wantsProductFact = fields.length || C.condition || C.price || C.stock || C.specs || C.included || C.battery || C.charger || C.pronoun;
      if (!group.length && this.ctx && wantsProductFact && !C.x_reco) group = this.ctx;

      // 3. Un ou plusieurs appareils sont nommés.
      if (group.length) {
        var keys = [];
        group.forEach(function (P) {
          if (keys.indexOf(P.key) === -1) keys.push(P.key);
        });

        if (keys.length > 1) {
          this.ctx = null;
          if (fields.length) return this.compare(group, fields);
          return this.list(group.slice(0, 4), 'Here are the cameras you mentioned:', group.length);
        }

        var cols = coloursIn(qf);
        if (cols.length && group.length > 1) {
          var tinted = group.filter(function (P) {
            return P.colours.some(function (c) { return cols.indexOf(c) !== -1; });
          });
          if (tinted.length) group = tinted;
        }
        this.ctx = group;

        if (C.condition) return this.condition(group);
        if ((C.included || C.battery || C.charger) && !(fields.length && !C.included)) {
          var inc = this.findFaq(qtoks, { included: true });
          if (inc) return this.faqAnswer(inc, qf, group[0]);
        }
        if (fields.length) return this.spec(group, fields);
        if (C.specs) return this.fullSpec(group);
        if (C.price || C.stock) return this.priceStock(group);

        var other = this.findFaq(qtoks, C);
        if (other && other.score >= 4) return this.faqAnswer(other, qf, null);
        return this.overview(group);
      }

      // 4. Recherche guidée : couleur, marque, budget, envie.
      var price = parsePrice(qf);
      var filt = {
        colours: coloursIn(qf),
        brands: brandsIn(qtoks),
        min: price.min,
        max: price.max,
        rare: !!C.x_rare,
        popular: !!C.x_popular,
        cheap: !!C.x_cheap,
        zoom: !!C.f_zoom,
        accessory: !!C.accessory && !C.included,
        features: Object.keys(FEATURES).filter(function (f) { return C[f]; })
      };
      var hasFilter = filt.colours.length || filt.brands.length || filt.min != null || filt.max != null || filt.rare || filt.popular || filt.cheap || filt.features.length || filt.accessory || (filt.zoom && C.x_reco);
      var faqHit = this.findFaq(qtoks, C);

      if (hasFilter && !(faqHit && faqHit.score >= 8 && !filt.colours.length && filt.max == null)) {
        this.ctx = null;
        return this.recommend(filt);
      }
      if (faqHit && faqHit.score >= 3.5) return this.faqAnswer(faqHit, qf, this.ctx && C.included ? this.ctx[0] : null);
      if (C.x_reco && this.products.length) {
        this.ctx = null;
        return this.recommend(filt);
      }
      if (C.pronoun && this.ctx) return this.overview(this.ctx);

      // 5. Dernier recours : recherche plein texte dans le catalogue,
      // avec un seuil haut pour ne pas montrer n'importe quoi.
      if (this.prodScore && mean.length) {
        var self = this;
        var ranked = this.products
          .map(function (P, i) { return [P, self.prodScore(i, mean)]; })
          .filter(function (x) { return x[0].price > 100 && x[1] >= 4; })
          .sort(function (a, b) { return b[1] - a[1]; });
        if (ranked.length) {
          return this.list(ranked.slice(0, 3).map(function (x) { return x[0]; }), 'This might be what you’re after:', ranked.length);
        }
      }

      return this.fallback();
    }

    identify(qtoks, qf) {
      if (!this.products.length) return [];
      var qset = new Set(qtoks);
      var best = 0;
      var scored = [];
      this.products.forEach(function (P) {
        var s = 0;
        P.models.forEach(function (m) {
          if (qset.has(m)) s += 10;
          else if (qtoks.some(function (t) { return t.length >= 3 && /\d/.test(t) && m !== t && m.indexOf(t) === 0; })) s += 6;
        });
        if (P.alias.length > 5 && qf.indexOf(P.alias) !== -1) s += 10;
        if (s && P.brand && qtoks.some(function (t) { return BRAND_OF[t] === P.brand; })) s += 1;
        if (s) {
          scored.push([P, s]);
          if (s > best) best = s;
        }
      });
      if (best < 6) return [];
      return scored
        .filter(function (x) { return x[1] >= best - 1; })
        .map(function (x) { return x[0]; })
        .sort(function (a, b) { return b.avail - a.avail; });
    }

    findFaq(qtoks, C) {
      var best = null;
      for (var i = 0; i < this.faq.length; i++) {
        var F = this.faq[i];
        var s = 0;
        F.concepts.forEach(function (c) {
          if (C[c]) s += 4;
        });
        s += this.faqScore(i, qtoks) + 0.3 * this.faqAScore(i, qtoks);
        if (!best || s > best.score) best = { f: F, score: s };
      }
      return best && best.score > 0 ? best : null;
    }

    /* ─────────── Réponses ─────────── */

    faqAnswer(hit, qf, P) {
      var F = hit.f;
      var text = String(F.a).split(/\r?\n/).filter(Boolean);
      var lead = [];
      var region = regionIn(qf);
      if (region) {
        var line = regionLine(F.a, region);
        if (line) lead.push({ s: line });
      }
      var note = '';
      if (P && P.isCamera) {
        lead.push('Yes — the ' + P.name + ' ships with it too, like every camera.');
        note = cardCaution(P);
      }
      var ans = { text: lead.concat(text), note: note };
      var url = safeUrl(F.lu);
      if (F.ll && url) ans.actions = [{ label: F.ll, url: url }];
      if (/gift|cadeau/i.test(F.q + ' ' + qf)) {
        ans.chips = ['Pink cameras', 'Our best sellers', 'Rare finds'];
      }
      return ans;
    }

    overview(group) {
      var P = group[0];
      var first = (P.desc.match(/^[^.!?]+[.!?]/) || [P.desc])[0].trim();
      var rows = ['f_sensor', 'f_zoom', 'f_screen', 'f_year']
        .map(function (id) {
          var v = fieldValue(P, id);
          return v ? [FIELDS[id].label === 'Zoom' ? 'Lens' : FIELDS[id].label, v] : null;
        })
        .filter(Boolean);
      var text = [];
      if (group.length > 1) text.push('We have ' + group.length + ' of these right now.');
      if (first) text.push(first);
      return {
        text: text,
        rows: rows,
        cards: group.slice(0, 4).map(card),
        chips: ['Condition of this one', 'Full specs', 'What’s in the box?']
      };
    }

    spec(group, fields) {
      var P = group[0];
      var said = [];
      var missing = [];
      fields.slice(0, 3).forEach(function (id) {
        // « zoom » et « objectif » lisent la même ligne : on ne la dit qu'une fois.
        if (id === 'f_lens' && fields.indexOf('f_zoom') !== -1) return;
        var v = fieldValue(P, id);
        if (v) said.push(FIELDS[id].say(P.name, v));
        else missing.push(FIELDS[id].label);
      });
      var text = said.slice();
      if (missing.length) {
        text.push(
          missing.join(' and ') + (missing.length > 1 ? ' aren’t' : ' isn’t') +
          ' listed on the ' + P.name + '’s sheet. I’d rather not guess — the team can check.'
        );
      }
      var ans = {
        text: text,
        note: fields.indexOf('f_storage') !== -1 ? cardCaution(P) : '',
        cards: [card(P)],
        chips: ['Full specs', 'Condition of this one']
      };
      if (missing.length) ans.actions = [{ label: 'Ask the team', url: this.cfg.contact }];
      return ans;
    }

    fullSpec(group) {
      var P = group[0];
      if (!P.spec.length) {
        return {
          text: ['There’s no technical sheet on the ' + P.name + ' yet. I’d rather not guess — the team can tell you.'],
          actions: [{ label: 'Ask the team', url: this.cfg.contact }]
        };
      }
      return {
        text: ['Here’s the sheet for the ' + P.name + ':'],
        rows: P.spec.filter(function (kv) { return kv[0]; }),
        cards: [card(P)],
        chips: ['Condition of this one', 'What’s in the box?']
      };
    }

    condition(group) {
      if (group.length === 1) {
        var P = group[0];
        var c = P.cond;
        if (c && (c.grade || c.rows.length)) {
          return {
            text: [
              c.grade
                ? 'This ' + P.name + ' is graded ' + c.grade + (c.label ? ' — ' + c.label : '') + '.'
                : 'Here’s the condition report for this ' + P.name + '.'
            ],
            rows: c.rows,
            note: c.note,
            cards: [card(P)],
            chips: ['How do you grade condition?', 'Full specs']
          };
        }
        return {
          text: [
            'This ' + P.name + ' hasn’t been individually graded yet.',
            'Every camera is cleaned, tested and fully functional before it ships. For photos or exact details on this unit, ask the team — a person replies within 12 hours.'
          ],
          cards: [card(P)],
          actions: [{ label: 'Ask for photos', url: this.cfg.contact }]
        };
      }
      return {
        text: ['We have ' + group.length + ' of these. Here’s each one:'],
        lines: group.map(function (P) {
          var c = P.cond;
          return unitName(P) + ': ' + (c && c.grade ? 'graded ' + c.grade + (c.label ? ' (' + c.label + ')' : '') : 'not individually graded yet');
        }),
        cards: group.slice(0, 4).map(card)
      };
    }

    priceStock(group) {
      if (group.length === 1) {
        var P = group[0];
        return {
          text: [
            'The ' + P.name + ' is ' + P.pm + (P.cm ? ' (was ' + P.cm + ')' : '') + ' and ' +
              (P.avail ? 'it’s in stock.' : 'it’s currently sold out.')
          ],
          cards: [card(P)],
          chips: P.avail ? ['Condition of this one', 'What’s in the box?'] : ['Show me other cameras']
        };
      }
      return {
        text: ['We have ' + group.length + ' of these:'],
        lines: group.map(function (P) {
          return unitName(P) + ' — ' + P.pm + (P.avail ? ', in stock' : ', sold out');
        }),
        cards: group.slice(0, 4).map(card)
      };
    }

    compare(group, fields) {
      var seen = {};
      var models = group.filter(function (P) {
        if (seen[P.key]) return false;
        seen[P.key] = true;
        return true;
      });
      var lines = [];
      fields.slice(0, 2).forEach(function (id) {
        models.forEach(function (P) {
          var v = fieldValue(P, id);
          lines.push(P.name + ' — ' + FIELDS[id].label.toLowerCase() + ': ' + (v || 'not listed'));
        });
      });
      return { text: ['Side by side:'], lines: lines, cards: models.slice(0, 4).map(card) };
    }

    recommend(f) {
      var self = this;
      var pool = this.products.filter(function (P) {
        if (P.price <= 100) return false; // articles de test à 1 €, jamais montrés
        return f.accessory ? !P.isCamera : P.isCamera;
      });
      var symbol = (pool[0] && String(pool[0].pm).replace(/[\d\s.,]/g, '')) || '€';

      pool = pool.filter(function (P) {
        if (f.colours.length && !P.colours.some(function (c) { return f.colours.indexOf(c) !== -1; })) return false;
        if (f.brands.length && f.brands.indexOf(P.brand) === -1) return false;
        if (f.max != null && P.price / 100 > f.max) return false;
        if (f.min != null && P.price / 100 < f.min) return false;
        if (f.rare && !P.rare) return false;
        if (f.popular && !P.popular) return false;
        return true;
      });

      var feat = function (P) {
        return f.features.reduce(function (n, id) {
          // On compte les indices distincts : un appareil décrit « after dark » et
          // stabilisé passe devant un appareil qui n'a que l'un des deux.
          return n + FEATURES[id].kw.filter(function (k) { return P.blob.indexOf(' ' + k + ' ') !== -1; }).length;
        }, 0);
      };
      if (f.features.length) {
        pool = pool.filter(function (P) { return feat(P) > 0; });
      }

      var inStock = pool.filter(function (P) { return P.avail; });
      var shown = inStock.length ? inStock : pool;

      shown.sort(function (a, b) {
        if (f.zoom && a.zoom !== b.zoom) return b.zoom - a.zoom;
        if (f.cheap) return a.price - b.price;
        var d = feat(b) - feat(a);
        if (d) return d;
        if (a.popular !== b.popular) return b.popular - a.popular;
        return a.price - b.price;
      });

      var noun = f.accessory ? 'accessories' : 'cameras';
      var adj = f.colours.concat(f.brands.map(function (b) { return BRAND_LABEL[b]; })).join(' ');
      var quals = [];
      if (f.min != null && f.max != null) quals.push('between ' + symbol + f.min + ' and ' + symbol + f.max);
      else if (f.max != null) quals.push('under ' + symbol + f.max);
      else if (f.min != null) quals.push('from ' + symbol + f.min);
      if (f.rare) quals.push('rare finds');
      if (f.popular) quals.push('best sellers');
      f.features.forEach(function (id) { quals.push(FEATURES[id].label); });
      if (f.zoom) quals.push('longest zoom first');
      else if (f.cheap) quals.push('cheapest first');
      if (inStock.length) quals.push('in stock');

      var all = { label: 'See every camera', url: (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'collections/all' };

      if (!shown.length) {
        var relax = [];
        if (f.colours.length) relax.push(f.colours[0][0].toUpperCase() + f.colours[0].slice(1) + ' cameras');
        if (f.max != null) relax.push('Cameras under ' + symbol + f.max);
        if (!relax.length) relax.push('Our best sellers');
        return {
          text: ['Nothing matches all of that right now. Here’s where you could look instead:'],
          chips: relax,
          actions: [all]
        };
      }

      // Un modèle n'occupe qu'une place : deux coloris du même appareil ne
      // sont pas deux suggestions. Le premier, déjà le mieux classé, reste.
      var keysSeen = {};
      shown = shown.filter(function (P) {
        if (keysSeen[P.key]) return false;
        keysSeen[P.key] = true;
        return true;
      });
      var top = shown.slice(0, 4);
      var head =
        'Here ' + (top.length === 1 ? 'is one' : 'are ' + top.length) +
        (shown.length > top.length ? ' of ' + shown.length : '') + ' ' +
        (adj ? adj + ' ' : '') + (top.length === 1 ? noun.replace(/ies$/, 'y').replace(/s$/, '') : noun) +
        (quals.length ? ' — ' + quals.join(', ') : '') + ':';
      var ans = { text: [head], cards: top.map(card), actions: [all] };
      if (!inStock.length) ans.note = 'None of these are in stock right now, but they show what we carry.';
      return ans;
    }

    list(products, head, total) {
      return { text: [head + (total > products.length ? ' (' + products.length + ' of ' + total + ')' : '')], cards: products.map(card) };
    }

    handoff() {
      var actions = [];
      if (this.cfg.tracking) actions.push({ label: 'Track my order', url: this.cfg.tracking });
      actions.push({ label: 'Contact the team', url: this.cfg.contact });
      return {
        text: [
          'That one needs a person — I can’t see orders or payments.',
          'Track your parcel with your order number, or write to us: a real person replies within 12 hours.'
        ],
        actions: actions
      };
    }

    fallback() {
      return {
        text: [
          'I don’t have a reliable answer to that, and I’d rather not guess.',
          'The team can help — a real person replies within 12 hours.'
        ],
        actions: [{ label: 'Contact the team', url: this.cfg.contact }],
        chips: this.chips.slice(0, 3)
      };
    }

    /* ─────────── Rendu ─────────── */

    push(entry) {
      this.history.push(entry);
      if (this.history.length > 40) this.history = this.history.slice(-40);
      this.render(entry, true);
      this.save();
    }

    typing() {
      var t = el('div', 'sai-asst__typing');
      t.setAttribute('aria-label', 'Typing');
      t.append(el('span'), el('span'), el('span'));
      this.logEl.append(t);
      this.scrollEnd();
      return t;
    }

    render(entry, live) {
      // Seules les suggestions du dernier message restent cliquables.
      this.logEl.querySelectorAll('.sai-asst__chips').forEach(function (c) { c.remove(); });

      if (entry.who === 'user') {
        var u = el('div', 'sai-asst__msg sai-asst__msg--user');
        u.append(el('p', null, entry.text));
        this.logEl.append(u);
        this.scrollEnd();
        return;
      }

      var a = entry.ans || {};
      var m = el('div', 'sai-asst__msg sai-asst__msg--bot');

      (a.text || []).forEach(function (t) {
        var p = el('p');
        if (t && typeof t === 'object') p.append(el('strong', null, t.s));
        else p.textContent = t;
        m.append(p);
      });

      if (a.lines && a.lines.length) {
        var ul = el('ul', 'sai-asst__lines');
        a.lines.forEach(function (l) { ul.append(el('li', null, l)); });
        m.append(ul);
      }

      if (a.rows && a.rows.length) {
        var dl = el('dl', 'sai-asst__rows');
        a.rows.forEach(function (r) {
          var row = el('div', 'sai-asst__row');
          row.append(el('dt', null, r[0]), el('dd', null, r[1]));
          dl.append(row);
        });
        m.append(dl);
      }

      if (a.note) m.append(el('p', 'sai-asst__note', a.note));

      if (a.cards && a.cards.length) {
        var wrap = el('div', 'sai-asst__cards');
        a.cards.forEach(function (c) {
          var href = safeUrl(c.u);
          if (!href) return;
          var link = el('a', 'sai-asst__card');
          link.href = href;
          var img = safeImg(c.img);
          if (img) {
            var i = el('img');
            i.src = img;
            i.alt = '';
            i.loading = 'lazy';
            i.width = 48;
            i.height = 48;
            link.append(i);
          } else {
            link.append(el('span', 'sai-asst__card-ph'));
          }
          var info = el('span');
          info.append(el('span', 'sai-asst__card-t', c.t));
          var meta = el('span', 'sai-asst__card-m');
          meta.append(el('span', null, c.pm));
          if (c.cm) meta.append(el('s', null, c.cm));
          meta.append(el('span', 'sai-asst__stock' + (c.a ? '' : ' sai-asst__stock--out'), c.a ? 'In stock' : 'Sold out'));
          info.append(meta);
          link.append(info);
          wrap.append(link);
        });
        m.append(wrap);
      }

      if (a.actions && a.actions.length) {
        var acts = el('div', 'sai-asst__actions');
        a.actions.forEach(function (ac) {
          var href = safeUrl(ac.url);
          if (!href) return;
          var l = el('a', 'sai-asst__link', ac.label);
          l.href = href;
          acts.append(l);
        });
        m.append(acts);
      }

      this.logEl.append(m);

      if (a.chips && a.chips.length) {
        var chips = el('div', 'sai-asst__chips');
        a.chips.forEach(function (q) {
          var b = el('button', 'sai-asst__chip', q);
          b.type = 'button';
          b.dataset.q = q;
          chips.append(b);
        });
        this.logEl.append(chips);
      }

      // Une réponse plus haute que la fenêtre se lit depuis son début.
      if (live && m.offsetHeight > this.logEl.clientHeight - 40) {
        this.logEl.scrollTop = m.offsetTop - 12;
      } else {
        this.scrollEnd();
      }
    }

    scrollEnd() {
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }

    /* ─────────── Mémoire de session ───────────
       La conversation suit le client d'une page à l'autre : cliquer sur
       une fiche proposée ne doit pas effacer ce qu'il venait de lire. */

    save() {
      try {
        sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({
            h: this.history,
            c: this.ctx ? this.ctx.map(function (P) { return P.h; }) : null
          })
        );
      } catch (e) {
        // Stockage refusé (navigation privée stricte) : on continue sans mémoire.
      }
    }

    restore() {
      var data;
      try {
        data = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      } catch (e) {
        data = null;
      }
      if (!data || !Array.isArray(data.h) || !data.h.length) return;
      this.history = data.h;
      this.history.forEach((e) => this.render(e, false));
      if (data.c && data.c.length) {
        this.loadKb().then(() => {
          var hs = data.c;
          var g = this.products.filter(function (P) { return hs.indexOf(P.h) !== -1; });
          if (g.length) this.ctx = g;
        });
      }
    }
  }

  window.customElements.define('sai-assistant', SaiAssistant);
})();
